import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import { useDispatch } from 'react-redux';
import FormProvider, { RHFCode, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { RouterLink } from 'src/routes/components';
// assets
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { useResetpasswordNewUserMutation } from "../../../store/Reducer/auth";
import { Box } from '@mui/material';
import { setUser } from 'src/store/slices/userSlice';


export default function JWTLoginNewPasswordView() {

  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useDispatch();
  const email = searchParams.get('email');
  const code = searchParams.get('activation_code');
  const [resetpasswordNewUser] = useResetpasswordNewUserMutation();
  const password = useBoolean();

  const NewPasswordSchema = Yup.object().shape({
    email: Yup.string().optional(),
    otp: Yup.string().required('Code is required'),
    password: Yup.string().required('password is required'),
    confirmPassword: Yup.string().required('Confirm Password is required')
      .oneOf([Yup.ref('password'), null], 'Passwords must match'),
  });

  const defaultValues = {
    otp: '',
    email: '',
    password: '',
    confirmPassword: '',
  };

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(NewPasswordSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const newData = { newpassword: data.password, confirmpassword: data.confirmPassword, email: data.email, otp: data.otp };
      const response = await resetpasswordNewUser(newData).unwrap();
      const user = response?.data;
      dispatch(setUser(user));
      if (user.user_type === '2') {
        router.push(paths.adminDashboard.root);
      } else if (user.user_type === '3' || user.user_type === '4') {
        router.push(paths.dashboard.root);
      } else if (user.user_type === '1') {
        router.push(paths.superAdminDashboard.root);
      }
      else {
        toast.error('Invalid user type');
      }
    } catch (error) {
      toast.error(error?.data?.message);
      console.error(error);
    }
  });

  useEffect(() => {
    email && setValue('email', email);
    code && setValue('otp', code);
  }, [email, code])


  const renderForm = (
    <Stack spacing={3} alignItems="center">
      <RHFCode name="otp" />
      <Box sx={{ display: "grid", gap: 2, width: "100%", gridTemplateColumns: { xs: "1fr", sm: "1fr" } }}>
        <RHFTextField
          name="password"
          label="Password"
          type={password.value ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={password.onToggle} edge="end">
                  <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <RHFTextField
          name="confirmPassword"
          label="Confirm New Password"
          type={password.value ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={password.onToggle} edge="end">
                  <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="primary"
        variant="contained"
        loading={isSubmitting}
      >
        Create Password
      </LoadingButton>

      <Link
        component={RouterLink}
        href={paths.auth.jwt.login}
        color="inherit"
        variant="subtitle2"
        sx={{
          alignItems: 'center',
          display: 'inline-flex',
        }}
      >
        <Iconify icon="eva:arrow-ios-back-fill" width={16} />
        Return to sign in
      </Link>
    </Stack>
  );

  const renderHead = (
    <>
      <Stack spacing={1} sx={{ my: 5 }}>
        <Typography variant="h3">New Password</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {/* Enter code and confirm your new password. */}
            Please enter the code sent to your <Typography variant="span" color="primary">{email}</Typography>, along with your password and confirm password, to verify your account.
          </Typography>
      </Stack>
    </>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}
      {renderForm}
    </FormProvider>
  );
}
